import { useStyletron } from '@thanx/uikit/theme'
import ProtectedPageHeader from 'components/ProtectedPageHeader'
import ScrollingTabNav from 'components/Tabs/ScrollingTabNav'
import TabItem from 'components/Tabs/TabItem'
import TabPane from 'components/Tabs/TabPane'
import { useHashTab } from 'components/Tabs/useHashTab'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import React from 'react'
import { Container } from 'react-bootstrap-five'
import Tab from 'react-bootstrap-five/Tab'
import Helmet from 'react-helmet'
import { I18n } from 'react-redux-i18n'
import LookerFooter from 'scenes/LookerEmbed/components/LookerFooter'
import LookerIframe from 'scenes/LookerEmbed/LookerIframe'
import { history } from 'store'
import AppInstallsPerLocationPerMonthTab from './AppInstallsPerLocationPerMonthTab'
import FirstPurchasesAndAppAdoptionByLocationTab from './FirstPurchasesAndAppAdoptionByLocationTab'

const t = buildTranslate('looker')

enum Tabs {
  APP_DOWNLOADS = 'app_downloads',
  APP_INSTALLS_PER_LOCATION_PER_MONTH = 'app_installs_per_location_per_month',
  FIRST_PURCHASES_AND_APP_ADOPTION_BY_LOCATION = 'first_purchases_and_app_adoption_by_location',
}

// This page had no tab structure before (a single inline Looker embed for
// dashboards/120, "App downloads"). That embed is now tab 1, unchanged. The
// page is already gated end-to-end on the
// narrower 'LocationReports' subject via ProtectedPageHeader's
// resource="LocationReports"/action="access" below — unlike
// ActivationRateReport/EngagementReport/CaptureRateReport (whose page-level
// gate resolves to the broader 'Reports' subject, or to a bespoke
// trial-eligibility rule), so there is nothing broader for the 2 new
// location-scoped tabs to need a narrower nested `ability.can` check
// against: if a user can reach this page's content at all, they already
// have 'LocationReports' access, so no additional per-tab gating is added.
// Tabs this PR adds, all behind the initiative flag. Resolving against the
// *visible* set is what gates them: with the flag off the only reachable tab
// is APP_DOWNLOADS, which is master's shape (a single inline embed).
const FLAG_GATED_TABS: Tabs[] = [
  Tabs.APP_INSTALLS_PER_LOCATION_PER_MONTH,
  Tabs.FIRST_PURCHASES_AND_APP_ADOPTION_BY_LOCATION,
]

const AppDownloadReport: React.FC = () => {
  const isPromoteInsightsLabReportsEnabled = useFlag(
    'mx-promote-insights-lab-reports',
    false
  )
  const merchant = useCurrentMerchant()
  const [css] = useStyletron()

  // Resolving against the *visible* set is what gates the flagged tabs: with
  // the flag off the only reachable id is APP_DOWNLOADS, so a deep link to a
  // gated tab falls back to master's shape.
  const visibleTabs = isPromoteInsightsLabReportsEnabled
    ? [Tabs.APP_DOWNLOADS, ...FLAG_GATED_TABS]
    : [Tabs.APP_DOWNLOADS]

  const [activeTab, setActiveTab] = useHashTab(visibleTabs, Tabs.APP_DOWNLOADS)

  const handleSwitchTab = (tab: Tabs) => {
    // Spread the current location: a bare `{ hash }` resets `search` to ''
    // (history 4.x createLocation), so switching tabs would drop the page's
    // query string.
    history.replace({ ...history.location, hash: tab })
    setActiveTab(tab)
  }

  if (!merchant) return null

  const title = t('urls.dashboards/120.title')
  const height = 1450

  return (
    <ProtectedPageHeader
      title={title}
      description=""
      resource="LocationReports"
      action="access"
    >
      <Helmet>
        <title>{I18n.t(`titles./reports/app_downloads#${activeTab}`)}</title>
      </Helmet>
      {isPromoteInsightsLabReportsEnabled ? (
        <Tab.Container
          activeKey={activeTab}
          transition={false}
          onSelect={(tab: string | null) => {
            if (!tab) return
            handleSwitchTab(tab as Tabs)
          }}
        >
          {/* ScrollingTabNav rather than TabNav: these labels are long enough
              to wrap onto a second row at common widths, which stops reading
              as a tab bar. Only the flag-ON branch has a tab row at all — the
              flag-OFF branch below reproduces master's tab-less markup and is
              deliberately left alone. */}
          <ScrollingTabNav ariaLabel={title}>
            <TabItem
              state={Tabs.APP_DOWNLOADS}
              title={t('urls.dashboards/120.title')}
            />
            <TabItem
              state={Tabs.APP_INSTALLS_PER_LOCATION_PER_MONTH}
              title={t('urls.dashboards/302.title')}
            />
            <TabItem
              state={Tabs.FIRST_PURCHASES_AND_APP_ADOPTION_BY_LOCATION}
              title={t('urls.dashboards/422.title')}
            />
          </ScrollingTabNav>
          <Tab.Content
            className={`pb-l h-100 grey-05-bkg ${css({
              minHeight: `calc(100% - 50px)`,
            })}`}
          >
            <TabPane
              state={Tabs.APP_DOWNLOADS}
              tabClassName={css({ minHeight: `${height + 300}px` })}
            >
              <LookerIframe
                path={'dashboards/120'}
                merchant={merchant}
                height={`${height}px`}
              />
              <LookerFooter className="my-xl" />
            </TabPane>
            <TabPane
              state={Tabs.APP_INSTALLS_PER_LOCATION_PER_MONTH}
              mountOnEnter
            >
              <AppInstallsPerLocationPerMonthTab />
            </TabPane>
            <TabPane
              state={Tabs.FIRST_PURCHASES_AND_APP_ADOPTION_BY_LOCATION}
              mountOnEnter
            >
              <FirstPurchasesAndAppAdoptionByLocationTab />
            </TabPane>
          </Tab.Content>
        </Tab.Container>
      ) : (
        <div
          className={`grey-05-bkg border-top-1 pb-l grey-20-border ${css({
            minHeight: `calc(100% - 50px)`,
          })}`}
        >
          <Container
            className={`px-0 ${css({
              minHeight: `${height + 300}px`,
            })}`}
          >
            <LookerIframe
              path={'dashboards/120'}
              merchant={merchant}
              height={`${height}px`}
            />
            <LookerFooter className="my-xl" />
          </Container>
        </div>
      )}
    </ProtectedPageHeader>
  )
}

export default AppDownloadReport
